import React, { useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PdfViewer from './PdfViewer';
import './ProjectPage.css';

const projectData = {
  'automated-microscore': 'Automated Microscore',
  'eledigm-venture': 'Eledigm Venture',
  'cad': 'Computer Aided Design Projects',
  'rainwater-harvesting': 'Rainwater Harvesting System',
  'mini-synthesizer': 'Mini-Synthesizer',
};

const projectDescriptions = {
  'amphicar-cad': '', // placeholder, not used
  'automated-microscore': '',
  'eledigm-venture': '',
  'cad': '',
  'rainwater-harvesting': 'As part of my high school Design & Technology course, I developed an autonomous rainwater harvesting and irrigation system optimized for resource efficiency. I conducted extensive research on water collection methods, filtration requirements, and gravity-fed distribution systems before designing a modular configuration of interconnected tanks and piping. The system utilized passive flow control to automatically distribute harvested rainwater to multiple plant zones without requiring external power or manual intervention. Through the design process, I applied principles of fluid dynamics, sustainable engineering, and systems integration while managing the project through multiple iterations from concept sketches to functional prototype. This project strengthened my abilities in technical research, CAD modeling, materials selection, and end-to-end project execution while deepening my understanding of sustainable water management systems.',
  'mini-synthesizer': 'During high school, I designed and built a functional analog synthesizer from the ground up. I modeled a custom casing and user interface in Autodesk Fusion 360, optimizing the layout for intuitive control of sound parameters including oscillator frequency, filter cutoff, and envelope shaping. I then assembled and soldered the circuit components (voltage-controlled oscillators, filters, and amplifiers) onto a PCB to generate and shape the audio signals. Through iterative testing and refinement, I developed a working prototype that successfully demonstrated core synthesis principles. This project strengthened my skills in CAD modeling, PCB assembly, and analog electronics while deepening my understanding of signal processing and sound synthesis.',
};

function ProjectPage() {
  const { projectId } = useParams();
  const projectName = projectData[projectId];
  const autoMicroscopeVideoRef = useRef(null);
  const autoMicroscopeVideoContainerRef = useRef(null);
  const eledigmVideoRef = useRef(null);
  const eledigmVideoContainerRef = useRef(null);
  const amphicarVideoRef = useRef(null);
  const amphicarVideoContainerRef = useRef(null);
  const amphicarVideo2Ref = useRef(null);
  const amphicarVideo2ContainerRef = useRef(null);
  const amphicarVideo3Ref = useRef(null);
  const amphicarVideo3ContainerRef = useRef(null);
  const amphicarVideo4Ref = useRef(null);
  const amphicarVideo4ContainerRef = useRef(null);
  const gravicarVideoRef = useRef(null);
  const gravicarVideoContainerRef = useRef(null);

  useEffect(() => {
    if (projectId !== 'automated-microscore' || !autoMicroscopeVideoContainerRef.current) return;
    const container = autoMicroscopeVideoContainerRef.current;
    const video = autoMicroscopeVideoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [projectId]);

  useEffect(() => {
    if (projectId !== 'eledigm-venture' || !eledigmVideoContainerRef.current) return;
    const container = eledigmVideoContainerRef.current;
    const video = eledigmVideoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [projectId]);

  useEffect(() => {
    if (projectId !== 'cad' || !amphicarVideoContainerRef.current) return;
    const container = amphicarVideoContainerRef.current;
    const video = amphicarVideoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [projectId]);

  useEffect(() => {
    if (projectId !== 'cad' || !amphicarVideo2ContainerRef.current) return;
    const container = amphicarVideo2ContainerRef.current;
    const video = amphicarVideo2Ref.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [projectId]);

  useEffect(() => {
    if (projectId !== 'cad' || !amphicarVideo3ContainerRef.current) return;
    const container = amphicarVideo3ContainerRef.current;
    const video = amphicarVideo3Ref.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [projectId]);

  useEffect(() => {
    if (projectId !== 'cad' || !amphicarVideo4ContainerRef.current) return;
    const container = amphicarVideo4ContainerRef.current;
    const video = amphicarVideo4Ref.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [projectId]);

  useEffect(() => {
    if (projectId !== 'cad' || !gravicarVideoContainerRef.current) return;
    const container = gravicarVideoContainerRef.current;
    const video = gravicarVideoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [projectId]);

  return (
    <div style={{
      padding: '2rem',
      maxWidth: '1200px',
      margin: '0 auto',
      background: '#1a1a1a',
      minHeight: '100vh',
      color: '#e0e0e0',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      marginTop: '2rem',
    }}>
      {projectName ? (
        <>
          <h1 style={{ color: '#00d4ff', textShadow: '0 0 15px rgba(0, 212, 255, 0.3)' }}>{projectName}</h1>
          {projectId === 'automated-microscore' && (
            <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              Research paper currently under review for publication: <a href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5403130" target="_blank" rel="noopener noreferrer" style={{ color: '#00d4ff', textDecoration: 'underline' }}>Link</a>
            </p>
          )}
          {projectId === 'cad' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem', marginTop: '2rem' }}>
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>1. Amphicar Model</h2>
                <p style={{ fontSize: '1.2rem', marginTop: '0.5rem', marginBottom: '2rem', whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                  Led a team of six in the design and development of a detailed, semi-functional Amphicar CAD model using SolidWorks, inspired by the original 1961 Amphicar, as part of a university engineering project. The assembly comprised 1,500+ parts and featured a fully integrated drivetrain system. I was responsible for engineering the drivetrain architecture, including the differential, 4-speed manual transmission, wheel hubs with suspension, and braking system, while collaborating on the vehicle body design. I also produced detailed GD&T manufacturing drawings for key components. The project received an overall grade of 85%+, ranking in the top 10 projects of the year group, and demonstrates my strengths in mechanical design, system integration, and technical leadership.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                    gap: '2rem',
                  }}>
                    {[
                      { type: 'image', src: 'amphicar_inside.PNG' },
                      { type: 'image', src: 'amphicar_insides.PNG' },
                      { type: 'video', src: 'Diff copy.mp4' },
                      { type: 'video', src: 'motor.mp4' },
                    ].map((item) => (
                      item.type === 'video' ? (
                        <div key={item.src} ref={item.src === 'Diff copy.mp4' ? amphicarVideoContainerRef : amphicarVideo4ContainerRef} style={{
                          width: '100%',
                          borderRadius: '12px',
                          boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                          background: '#222',
                          overflow: 'hidden',
                        }}>
                          <video
                            ref={item.src === 'Diff copy.mp4' ? amphicarVideoRef : amphicarVideo4Ref}
                            src={`/images/CAD-projects/Amphicar/${encodeURIComponent(item.src)}${item.src === 'Diff copy.mp4' ? '?v=2' : ''}`}
                            loop
                            muted
                            playsInline
                            style={{
                              width: '100%',
                              objectFit: 'cover',
                              maxHeight: '500px',
                              display: 'block',
                              borderRadius: '12px',
                            }}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      ) : (
                        <img
                          key={item.src}
                          src={`/images/CAD-projects/Amphicar/${item.src}`}
                          alt={`Amphicar model ${item.src}`}
                          style={{
                            width: '100%',
                            borderRadius: '12px',
                            boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                            background: '#222',
                            objectFit: 'cover',
                            maxHeight: '500px',
                          }}
                        />
                      )
                    ))}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '2rem',
                    flexWrap: 'wrap',
                  }}>
                    <div ref={amphicarVideo3ContainerRef} style={{ flex: 1, minWidth: 300 }}>
                      <video
                        ref={amphicarVideo3Ref}
                        src="/images/CAD-projects/Amphicar/wheel_assy.mp4?v=2"
                        loop
                        muted
                        playsInline
                        style={{
                          width: '100%',
                          minWidth: 300,
                          borderRadius: '12px',
                          boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                          background: '#222',
                          objectFit: 'cover',
                          maxHeight: '500px',
                          display: 'block',
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    <div ref={amphicarVideo2ContainerRef} style={{ flex: 1, minWidth: 300 }}>
                      <video
                        ref={amphicarVideo2Ref}
                        src={`/images/CAD-projects/Amphicar/${encodeURIComponent('Fixed Drive Train.mp4')}?v=2`}
                        loop
                        muted
                        playsInline
                        onLoadedMetadata={(e) => { e.target.playbackRate = 1.5; }}
                        style={{
                          width: '100%',
                          minWidth: 300,
                          borderRadius: '12px',
                          boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                          background: '#222',
                          objectFit: 'cover',
                          maxHeight: '500px',
                          display: 'block',
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                </div>
              </section>
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>2. Gravicar</h2>
                <p style={{ fontSize: '1.2rem', marginTop: '0.5rem', marginBottom: '2rem', lineHeight: 1.7 }}>
                  As part of a university manufacturing course (MANU 330), I designed and analyzed a composite chassis for a gravity-powered vehicle, optimizing for cost, manufacturability, and performance. Through material selection analysis, prototype testing, and structural evaluation, I determined that a sandwich panel construction with a PVC foam core and three GFRP layers per side provided optimal performance. I developed complete manufacturing documentation including engineering drawings for all components, work cell layouts, and factory floor planning. This project integrated composite materials knowledge, structural analysis, and design for manufacturing principles to deliver a production-ready design.
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                  gap: '2rem',
                }}>
                  {[
                    { type: 'image', src: 'car iso view.PNG' },
                    { type: 'image', src: 'car side view.PNG' },
                    { type: 'image', src: 'expl view sp4.PNG' },
                    { type: 'image', src: 'Capture.PNG' },
                    { type: 'image', src: 'gravi.jpg' },
                    { type: 'video', src: 'waterjet.mp4' },
                    { type: 'image', src: 'exploded view.PNG' },
                    { type: 'image', src: 'section drawing.PNG' },
                    { type: 'image', src: 'chassis drawing.PNG' },
                    { type: 'image', src: 'NEW wheel.PNG' },
                  ].map((item) => (
                    item.type === 'video' ? (
                      <div key={item.src} ref={gravicarVideoContainerRef} style={{
                        width: '100%',
                        borderRadius: '12px',
                        boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                        background: '#222',
                        overflow: 'hidden',
                      }}>
                        <video
                          ref={gravicarVideoRef}
                          src={`/images/CAD-projects/Gravicar/${encodeURIComponent(item.src)}?v=4`}
                          loop
                          muted
                          playsInline
                          onLoadedMetadata={(e) => { e.target.playbackRate = 1.75; }}
                          style={{
                            width: '100%',
                            objectFit: 'cover',
                            maxHeight: '500px',
                            display: 'block',
                            borderRadius: '12px',
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : (
                      <img
                        key={item.src}
                        src={`/images/CAD-projects/Gravicar/${item.src}`}
                        alt={`Gravicar ${item.src}`}
                        style={{
                          width: '100%',
                          borderRadius: '12px',
                          boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                          background: '#222',
                          objectFit: 'cover',
                          maxHeight: '500px',
                        }}
                      />
                    )
                  ))}
                </div>
              </section>
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>3. Mask Concept</h2>
                <p style={{ fontSize: '1.2rem', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                  As a first-year university project, I developed a concept for a multifunctional mask integrating HEPA filtration with innovative communication and hydration features. This design explored how personal protective equipment could evolve beyond basic safety requirements to address multiple user needs simultaneously, including clear communication, convenient hydration, and customizable aesthetics while maintaining high-efficiency particulate air filtration.
                </p>
                <PdfViewer file="/images/CAD-projects/Mask Concept/Mask_Concept.pdf" maxWidth={990} />
              </section>
            </div>
          )}
          {projectId === 'automated-microscore' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem', marginTop: '2rem' }}>
              {/* 1. Project Overview */}
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>1. Project Overview</h2>
                <p>During my UBC Manufacturing Engineering capstone, I collaborated with a team of five to develop an Automated Optical Module for in‑lab quality control. Integrated into the self‑driving characterization workflow, the module checks surface polish quality before samples are loaded into the Scanning Electron Microscope (SEM) for analysis. It automatically captures and stitches high‑resolution images. This pre‑screening flags surface defects or preparation issues, helping to reduce wasted SEM time.</p>
                <div className="overview-images-row">
                  <img src="/images/automated-microscore/section_1.1.png" alt="Overview 1" />
                  <img src="/images/automated-microscore/section_1.2.png" alt="Overview 2" className="overview-img-rotate" />
                </div>
                <div ref={autoMicroscopeVideoContainerRef} style={{ marginTop: '1.5rem' }}>
                  <h3 style={{ color: '#00d4ff', marginBottom: '0.75rem', fontSize: '1.1rem' }}>Scanning and Robotic Arm Sample Handling</h3>
                  <video
                    ref={autoMicroscopeVideoRef}
                    src="/images/automated-microscore/Automated_microscope_vid.mp4"
                    loop
                    muted
                    playsInline
                    onLoadedMetadata={(e) => { e.target.playbackRate = 1.8; }}
                    style={{
                      width: '100%',
                      maxWidth: 900,
                      borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                      background: '#222',
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </section>

              {/* 2. Design Objectives */}
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>2. Design Objectives</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.2rem', marginTop: '0.5rem' }}>
                  <div style={{ background: '#181a20', borderRadius: '10px', padding: '1.1rem 1.3rem', minWidth: '220px', flex: 1, boxShadow: '0 2px 8px rgba(0,212,255,0.07)', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <span style={{ fontSize: '1.5rem', color: '#00d4ff' }}>🔬</span>
                    <span>Detect scratches & surface debris &lt; 2 µm</span>
                  </div>
                  <div style={{ background: '#181a20', borderRadius: '10px', padding: '1.1rem 1.3rem', minWidth: '220px', flex: 1, boxShadow: '0 2px 8px rgba(0,212,255,0.07)', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <span style={{ fontSize: '1.5rem', color: '#00d4ff' }}>🤖</span>
                    <span>Automated high‑resolution imaging, no operator input</span>
                  </div>
                  <div style={{ background: '#181a20', borderRadius: '10px', padding: '1.1rem 1.3rem', minWidth: '220px', flex: 1, boxShadow: '0 2px 8px rgba(0,212,255,0.07)', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <span style={{ fontSize: '1.5rem', color: '#00d4ff' }}>⏱️</span>
                    <span>Reduce SEM cycle delays and faulty data</span>
                  </div>
                  <div style={{ background: '#181a20', borderRadius: '10px', padding: '1.1rem 1.3rem', minWidth: '220px', flex: 1, boxShadow: '0 2px 8px rgba(0,212,255,0.07)', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <span style={{ fontSize: '1.5rem', color: '#00d4ff' }}>🖼️</span>
                    <span>Stitched optical images, complementary to SEM</span>
                  </div>
                  <div style={{ background: '#181a20', borderRadius: '10px', padding: '1.1rem 1.3rem', minWidth: '220px', flex: 1, boxShadow: '0 2px 8px rgba(0,212,255,0.07)', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <span style={{ fontSize: '1.5rem', color: '#00d4ff' }}>🔗</span>
                    <span>Modular integration in self‑driving workflows</span>
                  </div>
                  <div style={{ background: '#181a20', borderRadius: '10px', padding: '1.1rem 1.3rem', minWidth: '220px', flex: 1, boxShadow: '0 2px 8px rgba(0,212,255,0.07)', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <span style={{ fontSize: '1.5rem', color: '#00d4ff' }}>📏</span>
                    <span>Sample sizes: 1 mm² up to 4 cm²</span>
                  </div>
                </div>
              </section>

              {/* 3. Key Features */}
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>3. Key Features</h2>
                <ul style={{ fontSize: '1.1rem', lineHeight: 1.7, marginLeft: '0', listStyle: 'none', padding: 0 }}>
                  <li style={{ marginBottom: '1.1em' }}>
                    <div>
                      <span style={{ fontWeight: 700 }}>Low-Cost, DIY Build</span><br />
                      <span style={{ marginLeft: '1.5em', display: 'flex', alignItems: 'center', gap: '0.5em' }}><span style={{ color: '#00d4ff', fontSize: '1em' }}>•</span>Total material cost ~CA$1,600; uses Arduino, Raspberry Pi, stepper motors, and T-slot aluminum extrusions.</span>
                    </div>
                  </li>
                  <li style={{ marginBottom: '1.1em' }}>
                    <div>
                      <span style={{ fontWeight: 700 }}>Precise Motorized Motion</span><br />
                      <span style={{ marginLeft: '1.5em', display: 'block' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}><span style={{ color: '#00d4ff', fontSize: '1em' }}>•</span>CoreXY system for X-Y positioning (13.3 µm steps)</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}><span style={{ color: '#00d4ff', fontSize: '1em' }}>•</span>Ball-screw Z-axis (2.5 µm focus increments)</span>
                      </span>
                    </div>
                  </li>
                  <li style={{ marginBottom: '1.1em' }}>
                    <div>
                      <span style={{ fontWeight: 700 }}>Automated Focus & Stitching</span><br />
                      <span style={{ marginLeft: '1.5em', display: 'block' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}><span style={{ color: '#00d4ff', fontSize: '1em' }}>•</span>Laplacian-variance autofocus routine</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}><span style={{ color: '#00d4ff', fontSize: '1em' }}>•</span>Real-time image-stitching via Fiji/ImageJ</span>
                      </span>
                    </div>
                  </li>
                  <li>
                    <div>
                      <span style={{ fontWeight: 700 }}>Two Imaging Modes</span><br />
                      <span style={{ marginLeft: '1.5em', display: 'block' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}><span style={{ color: '#00d4ff', fontSize: '1em' }}>•</span>Random Sampling – spot-check defect detection</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}><span style={{ color: '#00d4ff', fontSize: '1em' }}>•</span>Full-Field Scanning – mosaic of the entire sample</span>
                      </span>
                    </div>
                  </li>
                </ul>
                <div style={{ marginTop: '1.2rem' }}>
                  <img src="/images/automated-microscore/section_3.1.png" alt="Key Features" style={{ maxWidth: '630px', width: '100%', borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,212,255,0.10)' }} />
                </div>
              </section>

              {/* 4. Technical Specifications */}
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>4. Technical Specifications</h2>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#00d4ff', marginBottom: '1.2rem' }}>Module Parameters</div>
                <table style={{ width: '100%', background: '#181a20', borderRadius: '14px', boxShadow: '0 2px 16px rgba(0,212,255,0.10)', marginBottom: '1.2rem', fontSize: '1.08rem', borderCollapse: 'separate', borderSpacing: 0 }}>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: 700, padding: '0.8em 1.5em', color: '#fff', minWidth: '180px', border: 'none', background: 'transparent' }}>Imaging Area</td>
                      <td style={{ padding: '0.8em 1.5em', color: '#e0e0e0', border: 'none', background: 'transparent' }}>150 mm × 150 mm</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 700, padding: '0.8em 1.5em', color: '#fff', border: 'none', background: 'transparent' }}>Pixel Resolution</td>
                      <td style={{ padding: '0.8em 1.5em', color: '#e0e0e0', border: 'none', background: 'transparent' }}>1.5 µm × 1.5 µm per pixel</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 700, padding: '0.8em 1.5em', color: '#fff', border: 'none', background: 'transparent' }}>X–Y Positioning</td>
                      <td style={{ padding: '0.8em 1.5em', color: '#e0e0e0', border: 'none', background: 'transparent' }}>26.5 µm per micro-step (up to 13.3 µm with 1/16 micro-stepping)</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 700, padding: '0.8em 1.5em', color: '#fff', border: 'none', background: 'transparent' }}>Z-Axis Resolution</td>
                      <td style={{ padding: '0.8em 1.5em', color: '#e0e0e0', border: 'none', background: 'transparent' }}>2.5 µm per micro-step</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 700, padding: '0.8em 1.5em', color: '#fff', border: 'none', background: 'transparent' }}>Optics</td>
                      <td style={{ padding: '0.8em 1.5em', color: '#e0e0e0', border: 'none', background: 'transparent' }}>Raspberry Pi HQ Camera (12.3 MP CS-mount) + Adjustable LED ring light</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 700, padding: '0.8em 1.5em', color: '#fff', border: 'none', background: 'transparent' }}>Motion Control</td>
                      <td style={{ padding: '0.8em 1.5em', color: '#e0e0e0', border: 'none', background: 'transparent' }}>CoreXY belt drive (X–Y) + ball-screw Z-axis, driven by NEMA 17 steppers</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 700, padding: '0.8em 1.5em', color: '#fff', border: 'none', background: 'transparent' }}>Electronics</td>
                      <td style={{ padding: '0.8em 1.5em', color: '#e0e0e0', border: 'none', background: 'transparent' }}>Arduino Uno + CNC Shield; Raspberry Pi 5; LM2596 buck converters for 24 V, 12 V, and 5 V rails</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 700, padding: '0.8em 1.5em', color: '#fff', border: 'none', background: 'transparent' }}>Software</td>
                      <td style={{ padding: '0.8em 1.5em', color: '#e0e0e0', border: 'none', background: 'transparent' }}>Python GUI (Tkinter + OpenCV), Fiji/ImageJ for stitching, ROS-2 compatible</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              {/* 5. Mechanical & Optical Design */}
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>5. Mechanical & Optical Design</h2>
                <p>The module’s chassis uses modular T‑slot extrusions and corner brackets to form a stable base. X‑Y carriage movement is driven by a CoreXY belt system: dual NEMA 17 steppers, tension GT2 belts over precision MGN rails. Vertical adjustments rely on an M8 ball screw paired with linear guides, delivering smooth 2.5 µm focus steps.</p>
                <div className="mechanical-design-row">
                  <img src="/images/automated-microscore/section_5.1.png" alt="Mechanical Design 1" />
                  <img src="/images/automated-microscore/section_5.2.png" alt="Mechanical Design 2" />
                </div>
                <div className="mechanical-design-row-with-text">
                  <div style={{ flex: 1, minWidth: '280px' }}>
                    <p>The picture on the right shows an exploded view of the camera carriage. Inside the carraige, a Raspberry Pi HQ camera is mounted to a CS‑mount microscope lens. An LED ring light encircles the lens, offering uniform illumination. Three‑screw at the bottom of the lens allow for fine alignment of the optical axis. As the carriage traverses, images overlap by design and are later merged into a full‑field montage.</p>
                  </div>
                  <img src="/images/automated-microscore/section_5.3.png" alt="Mechanical Design 3" className="mechanical-design-img" />
                </div>
              </section>

              {/* 6. Electronics & Control */}
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>6. Electronics & Control</h2>
                <p>All motion and power management is handled through off‑the‑shelf electronics. Three NEMA 17 stepper motors connect to an Arduino Uno + CNC Shield, receiving 24 V via an LM2596 buck converter for high‑torque X‑Y and Z‑axis drives. A second converter provides 12 V to the LED ring light, and a third delivers 5 V to the Raspberry Pi 5, which orchestrates high‑level routines and image capture. Safety is built in with an E‑Stop wired before the DC supply, and limit switches on each axis enable reliable homing and collision prevention.</p>
                <div style={{ marginTop: '1.2rem', display: 'flex', justifyContent: 'center' }}>
                  <img src="/images/automated-microscore/section_6.1.png" alt="Electronics & Control" style={{ maxWidth: '700px', width: '100%', borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,212,255,0.10)' }} />
                </div>
              </section>

              {/* 7. Software and Automation */}
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>7. Software and Automation</h2>
                <div className="software-section-main">
                  <div style={{ flex: 1, minWidth: '260px' }}>
                    <p>A custom Python GUI (built with Tkinter and OpenCV) provides straightforward controls for both Random Sampling and Full‑Field Scanning. Users simply enter sample parameters, choose a mode, and the system handles the rest:</p>
                    <ol style={{ fontSize: '1.1rem', lineHeight: 1.7, marginLeft: '1.2rem' }}>
                      <li>Auto‑focus uses a Laplacian‑variance score on median‑blurred frames to drive the Z‑axis to the sharpest plane.</li>
                      <li>Image Capture grabs overlapping frames across the sample in a grid pattern.</li>
                      <li>Real‑time Stitching leverages Fiji/ImageJ APIs to merge individual captures into a seamless montage.</li>
                    </ol>
                  </div>
                  <img src="/images/automated-microscore/section_7.1.jpeg" alt="Software GUI" className="software-gui-img" />
                </div>
                <div className="software-stitching-row">
                  <img src="/images/automated-microscore/section_7.2.png" alt="Software Stitching 1" />
                  <img src="/images/automated-microscore/section_7.3.png" alt="Software Stitching 2" />
                </div>
                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}>
                  <img src="/images/automated-microscore/section_8.1.png" alt="Focus Score for Auto-Focusing" style={{ maxWidth: '600px', width: '100%', borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,212,255,0.10)' }} />
                </div>
              </section>

              {/* 8. Performance & Results */}
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>8. Performance & Results</h2>
                <p>Validation Summary: In a series of test runs, including polished pucks and a Canadian “toonie”, every focus score exceeded 1.0, ensuring edge clarity and defect visibility. Full‑field scans of a 4 cm² area (25 frames) stitched in under six minutes from start to finish.</p>
                <div className="perf-results-wrapper">
                <table className="perf-results-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Sample</th>
                      <th style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Mode</th>
                      <th style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Focus Score</th>
                      <th style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Defects Detected</th>
                      <th style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Stitch Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Polished Puck</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Scanning</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>1.79</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Yes</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>3 m 42 s</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Toonie Coin</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Scanning</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>12.49</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Yes</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>5 m 28 s</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Puck (Random)</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Sampling</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>14.6</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>Yes</td>
                      <td style={{ textAlign: 'left', padding: '0.7em 1.2em 0.7em 0' }}>—</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </section>

              {/* 9. Gallery */}
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>9. Gallery</h2>
                <h3 style={{ color: '#00d4ff', marginTop: '1.2rem' }}>Examples of stitched images</h3>
                <div className="gallery-stitched-row gallery-stitched-row-2">
                  <img src="/images/automated-microscore/section_9.1.png" alt="Stitched Example 1" />
                  <img src="/images/automated-microscore/section_9.2.png" alt="Stitched Example 2" />
                </div>
                <div className="gallery-stitched-row gallery-stitched-row-3">
                  <img src="/images/automated-microscore/section_9.3.jpg" alt="Stitched Example 3" />
                  <img src="/images/automated-microscore/section_9.4.jpg" alt="Stitched Example 4" />
                  <img src="/images/automated-microscore/section_9.45.jpg" alt="Stitched Example 5" />
                </div>
                <h3 style={{ color: '#00d4ff', marginTop: '1.2rem' }}>Photographs of the physical model</h3>
                <div className="gallery-photos-row">
                  <img src="/images/automated-microscore/section_9.5.jpeg" alt="Model 1" />
                  <img src="/images/automated-microscore/section_9.6.jpeg" alt="Model 2" />
                </div>
                <div className="gallery-photos-detail-row">
                  <div className="gallery-photos-detail-item">
                    <img src="/images/automated-microscore/section_9.7.jpg" alt="Model 3" />
                    <span style={{ fontSize: '0.95em', color: '#b0b0b0', marginTop: '0.4em' }}>Camera Carriage</span>
                  </div>
                  <div className="gallery-photos-detail-item">
                    <img src="/images/automated-microscore/section_9.8.jpg" alt="Model 4" />
                    <span style={{ fontSize: '0.95em', color: '#b0b0b0', marginTop: '0.4em' }}>Lens Adjustment Screws</span>
                  </div>
                  <div className="gallery-photos-detail-item">
                    <img src="/images/automated-microscore/section_9.9.jpg" alt="Model 6" />
                    <span style={{ fontSize: '0.95em', color: '#b0b0b0', marginTop: '0.4em' }}>Ball Screw</span>
                  </div>
                </div>
              </section>

              {/* 10. Current Research & Acknowledgments */}
              <section>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>10. Current Research & Acknowledgments</h2>
                <p><b>Project Team:</b> Mikhail Ignatyev, Stephanie Monagan, Harry Zhou, Brady Kreil, Angus Hawley, Andrey Chirikov</p>
                <p><b>Supervision & Support:</b> Prof. Ben Britton, Graeme Francolini</p>
                <p>During this capstone project, I led the mechanical design - creating all CAD models and renderings; researching microscopy, optics, camera, and lens options; and assisting with the Raspberry Pi setup. I also designed and iterated key subassemblies, fabricated parts through 3D printing, and oversaw the final assembly. Additionally, our team has submitted a manuscript to a scientific journal HardwareX. Currently, the research paper is accessible publicly, but has not been peer-reviewed yet.</p>
                <p><b>Funding:</b><br />UBC Department of Manufacturing Engineering (CA$650)<br />UBC Department of Materials Engineering (CA$740)</p>
                <p>More information on the project: <a href="https://github.com/ManuCapstone1/OpticalModule" target="_blank" rel="noopener noreferrer" style={{ color: '#00d4ff', textDecoration: 'underline' }}>GitHub</a><br />Research paper currently under review for publication: <a href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5403130" target="_blank" rel="noopener noreferrer" style={{ color: '#00d4ff', textDecoration: 'underline' }}>Link</a></p>
              </section>
            </div>
          )}
          {projectId === 'mini-synthesizer' && (
            <>
              <div style={{
                display: 'flex',
                gap: '2rem',
                alignItems: 'flex-start',
                marginTop: '1rem',
                marginBottom: '2rem',
                flexWrap: 'wrap',
              }}>
                <p style={{ fontSize: '1.2rem', lineHeight: 1.7, flex: '1 1 300px', margin: 0 }}>
                  {projectDescriptions['mini-synthesizer']}
                </p>
                <div style={{ flex: '0 1 400px', minWidth: 0 }}>
                  <img
                    src="/images/mini-synthesizer/Mini_pic.jpg"
                    alt="Mini-Synthesizer"
                    style={{
                      width: '100%',
                      maxHeight: '500px',
                      objectFit: 'contain',
                      borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                      background: '#222',
                    }}
                  />
                </div>
              </div>
              <PdfViewer file="/images/mini-synthesizer/mini_synthesizer.pdf" title="Project report" />
            </>
          )}
          {projectId === 'rainwater-harvesting' && (
            <>
              <div style={{
                display: 'flex',
                gap: '2rem',
                alignItems: 'flex-start',
                marginTop: '1rem',
                marginBottom: '2rem',
                flexWrap: 'wrap',
              }}>
                <p style={{ fontSize: '1.2rem', lineHeight: 1.7, flex: '1 1 300px', margin: 0 }}>
                  {projectDescriptions['rainwater-harvesting']}
                </p>
                <div style={{ flex: '0 1 400px', minWidth: 0 }}>
                  <img
                    src="/images/rainwater-harvesting/rainwater.png"
                    alt="Rainwater Harvesting System"
                    style={{
                      width: '100%',
                      maxHeight: '500px',
                      objectFit: 'contain',
                      borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                      background: '#222',
                    }}
                  />
                </div>
              </div>
              <PdfViewer file="/images/rainwater-harvesting/rainwater_harvesting.pdf" title="Project report" maxWidth={800} />
            </>
          )}
          {projectId === 'eledigm-venture' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '2rem' }}>
              <p style={{ fontSize: '1.2rem', lineHeight: 1.7, marginTop: '0.5rem', marginBottom: 0 }}>
                As Co-Founder of Eledigm, I led a 4-member engineering team to address critical water contamination issues affecting rural and Indigenous communities across Canada. Our work secured $25,000 in combined funding, including $10,000 from UBC's Innovation, Entrepreneurship & Impact Fund and $15,000 in seed funding from the AquaHacking challenge finals. Through the 'Foresight' incubator program, I developed essential entrepreneurship skills in sales and marketing while managing extensive stakeholder engagement, conducting over 200 interviews and calls to validate our solution and build a robust client network. Working collaboratively with my team, I contributed to designing and fabricating a 3D-printed prototype using SolidWorks, transforming our concept from initial research into a tangible product ready for market deployment.
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '2rem',
              }}>
                <img
                  src={`/images/eledigm-venture/${encodeURIComponent('Untitled Project 2.png')}`}
                  alt="Eledigm Venture"
                  style={{
                    width: '100%',
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                    background: '#222',
                    objectFit: 'cover',
                    maxHeight: '500px',
                  }}
                />
                <img
                  src={`/images/eledigm-venture/${encodeURIComponent('Exploded viewp-1.png')}`}
                  alt="Eledigm Venture Exploded view"
                  style={{
                    width: '100%',
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                    background: '#222',
                    objectFit: 'cover',
                    maxHeight: '500px',
                  }}
                />
              </div>
              <p style={{ fontSize: '1.2rem', lineHeight: 1.7, marginTop: 0, marginBottom: 0 }}>
                WaterSafe is a retrofittable cistern attachment designed to eliminate water contamination in rural households that rely on water tanks rather than municipal treatment systems. Our market research revealed that 50% of rural and Indigenous communities experienced contamination ranging from debris to trapped animals, creating serious health risks. The solution features a sealed base with a raised, downward-tilted entrance pipe that prevents foreign objects from entering while accommodating multiple hose sizes (2-4 inches) for seamless water delivery. The system integrates ultrasonic and temperature sensors connected to a cross-platform mobile app, enabling remote monitoring of water levels and temperature to prevent flooding, freezing, and supply depletion. With no comparable products on the market and a price point of $100-150, WaterSafe offers a cost-effective alternative to bottled water ($1,376/year) while reducing environmental impact and empowering communities with reliable, safe water access.
              </p>
              <div style={{
                display: 'flex',
                gap: '2rem',
                flexWrap: 'wrap',
              }}>
                <img
                  src={`/images/eledigm-venture/${encodeURIComponent('cis.png')}`}
                  alt="Eledigm Venture CIS"
                  style={{
                    flex: 1,
                    minWidth: 300,
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                    background: '#222',
                    objectFit: 'cover',
                    maxHeight: '500px',
                  }}
                />
                <div ref={eledigmVideoContainerRef} style={{ flex: 1, minWidth: 300 }}>
                <video
                  ref={eledigmVideoRef}
                  src={`/images/eledigm-venture/${encodeURIComponent('Hydralic Cylinder Motion Study.mp4')}`}
                  loop
                  muted
                  playsInline
                  style={{
                    width: '100%',
                    minWidth: 300,
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                    background: '#222',
                  }}
                >
                  Your browser does not support the video tag.
                </video>
                </div>
              </div>
              <section style={{ marginTop: 0 }}>
                <h2 style={{ color: '#00d4ff', marginBottom: '1rem', marginTop: 0 }}>Business Solution Overview</h2>
                <PdfViewer file={`/images/eledigm-venture/${encodeURIComponent('2 Ignatyev.Mikhail._Eledigm.pdf')}`} maxWidth={990} />
              </section>
            </div>
          )}
          {projectId !== 'cad' && projectId !== 'automated-microscore' && projectId !== 'mini-synthesizer' && projectId !== 'rainwater-harvesting' && projectId !== 'eledigm-venture' && (
            <p style={{ fontSize: '1.2rem', marginTop: '2rem' }}>Project details coming soon...</p>
          )}
        </>
      ) : (
        <h2>Project not found</h2>
      )}
    </div>
  );
}

export default ProjectPage; 